import { Mic, FileText, Shield, Database, Clock, Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { ScrollReveal } from './ScrollReveal';

const featureIcons = [
  Mic,
  FileText,
  Shield,
  Database,
  Clock,
  Languages,
];

interface FeaturesProps {
  showIntro?: boolean;
}

export function Features({ showIntro = true }: FeaturesProps) {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const features = [
    {
      icon: featureIcons[0],
      titleKey: 'features.recording.title',
      descriptionKey: 'features.recording.description',
    },
    {
      icon: featureIcons[1],
      titleKey: 'features.transcription.title',
      descriptionKey: 'features.transcription.description',
    },
    {
      icon: featureIcons[2],
      titleKey: 'features.local.title',
      descriptionKey: 'features.local.description',
    },
    {
      icon: featureIcons[3],
      titleKey: 'features.archive.title',
      descriptionKey: 'features.archive.description',
    },
    {
      icon: featureIcons[4],
      titleKey: 'features.stats.title',
      descriptionKey: 'features.stats.description',
    },
    {
      icon: featureIcons[5],
      titleKey: 'features.languages.title',
      descriptionKey: 'features.languages.description',
    },
  ];

  return (
    <section
      id="features"
      className="py-20 bg-white"
      style={{ scrollMarginTop: isMobile ? '140px' : '104px' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {showIntro ? (
          <div className="text-center max-w-3xl mx-auto mb-16">
            <ScrollReveal>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('features.title')}
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={1}>
              <p className="text-lg text-gray-600">
                {t('features.subtitle')}
              </p>
            </ScrollReveal>
          </div>
        ) : null}
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <ScrollReveal key={index} delay={(index % 6) + 1}>
              <div className="p-6 rounded-xl border border-gray-200 hover:border-teal-300 hover:shadow-lg transition-all">
                <div className="bg-teal-100 rounded-lg p-3 inline-block mb-4">
                  <feature.icon className="size-6 text-teal-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {t(feature.titleKey)}
                </h3>
                <p className="text-gray-600">
                  {t(feature.descriptionKey)}
                </p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}
